import { HttpParams } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { AnalyticsType } from 'src/app/enums/analytics-type';
import { CardType } from 'src/app/enums/card-type';
import { Project } from 'src/app/interfaces/project';
import { Ticket } from 'src/app/interfaces/ticket';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { ErrorService } from 'src/app/services/error.service';
import { LocationService } from 'src/app/services/location.service';
import { TicketService } from 'src/app/services/ticket.service';
import { UploadService } from 'src/app/services/upload.service';
import { DateHelper } from 'src/app/utils/date-helper';
import { TranslateModule } from '@ngx-translate/core';
import { LoaderComponent } from '../../../../components/loader/loader.component';
import { FileInputComponent } from '../../../../components/file-input/file-input.component';
import { FormGroupComponent } from '../../../../components/form-group/form-group.component';
import { DatePipe, NgFor, NgIf } from '@angular/common';
import { CardComponent } from '../../../../components/cards/card/card.component';
import { DivClickDirective } from 'src/app/directives/div-click.directive';
import { InlineSVGModule } from 'ng-inline-svg-2';

@Component({
  selector: 'app-project-contact',
  templateUrl: './project-contact.component.html',
  standalone: true,
  imports: [
    CardComponent,
    NgIf,
    NgFor,
    DatePipe,
    FormsModule,
    ReactiveFormsModule,
    FormGroupComponent,
    FileInputComponent,
    LoaderComponent,
    TranslateModule,
    DivClickDirective,
    InlineSVGModule,
  ],
})
export class ProjectContactComponent implements OnInit {
  CardType = CardType;
  contactForm: FormGroup;
  loading: boolean = false;
  uploadedFile: string | File;
  chatEnabled: boolean = true;
  tickets: Ticket[] = [];
  DateHelper = DateHelper;
  @Input() project: Project;
  @Input() active: boolean;
  @Output() switchTab: EventEmitter<string> = new EventEmitter<string>();

  constructor(
    private formBuilder: FormBuilder,
    private errorService: ErrorService,
    private uploadService: UploadService,
    private locationService: LocationService,
    private ticketService: TicketService,
    private analyticsService: AnalyticsService,
    private router: Router
  ) {
    this.createContactForm();
  }

  ngOnInit() {
    if (this.project) {
      this.chatEnabled = this.project.chatEnabled;
    }

    this.ticketService.replied$.subscribe(() => {
      this.getTickets();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.active?.currentValue) {
      this.getTickets();
    }
  }

  async getTickets() {
    this.tickets = [];
    this.loading = true;
    try {
      const response = await this.ticketService.getAllTickets(
        this.setURLParams(),
        this.project
      );
      this.tickets = response.member;
      this.tickets.sort(
        (c1, c2) =>
          c2.lastInteractionDate.getTime() - c1.lastInteractionDate.getTime()
      );
    } catch (error) {
      // ignore
    } finally {
      this.loading = false;
      this.ticketService.updateLastViewData(this.project);
    }
  }

  setURLParams() {
    return new HttpParams()
      .set('status', 'ALL')
      .set('order[updatedAt]', 'desc');
  }

  createContactForm() {
    this.contactForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.maxLength(250)]],
      subject: ['', [Validators.required, Validators.maxLength(250)]],
      message: ['', [Validators.required, Validators.maxLength(10000)]],
      attachment: [''],
    });
  }

  openTicket(ticket: Ticket) {
    this.router.navigate(
      ['projects', this.project.slug, 'conversations', ticket.id],
      {
        state: { ticket: ticket },
      }
    );
  }

  public setUploadedFile(file: string | File) {
    this.uploadedFile = file;
  }

  async send() {
    this.errorService.markFormGroupTouchedAndDirty(this.contactForm);

    if (this.contactForm.valid) {
      this.loading = true;

      try {
        const data = this.contactForm.value;
        let attachment = null;

        if (this.uploadedFile != null) {
          const uploadResult: any = await this.uploadService.upload(
            'ticket-message-attachment',
            this.uploadedFile
          );

          attachment = uploadResult.file;
        }

        let location: GeolocationPosition | null = null;

        try {
          location = await this.locationService.getCurrentPosition();
        } catch (error) {
          // no location available, but continue
        }

        const ticket: Ticket = {
          project: this.project,
          messages: [
            {
              content: data.message,
              attachment,
            },
          ],
          name: data.name,
          subject: data.subject,
          locationLat: location === null ? null : location.coords.latitude,
          locationLong: location === null ? null : location.coords.longitude,
        };

        this.ticketService.saveName(data.name);

        const result = await this.ticketService.createTicket(ticket);

        ticket.lastInteractionDate = new Date();
        ticket.id = result.id;
        this.tickets.unshift(result);

        this.contactForm.reset();

        this.log(data);
      } catch (error) {
        this.errorService.parseErrorsToForm(this.contactForm, error, {
          attachmentFile: 'attachment',
        });
      } finally {
        this.loading = false;
      }
    }
  }

  private log(formValue: any) {
    this.analyticsService.logEvent(
      AnalyticsType.CONTACT_MESSAGE,
      {
        name: formValue.name,
        subject: formValue.subject,
        attached_media: formValue.attachment,
        action: 'create',
      },
      this.project
    );
  }
}
